import { useState } from "react";
import { euro, kgf, toNum } from "@/lib/excelCalc";

export default function RecipeRoundPlanner({ recipe, ingredientPrijs }) {
  const ingredienten = recipe.ingredienten || [];
  const standaardRondeKg = ingredienten.reduce(
    (total, item) => total + toNum(item.kgRondje),
    0,
  );
  const [modus, setModus] = useState("kg");
  const [doelKg, setDoelKg] = useState("");
  const [rondes, setRondes] = useState("");
  const [rondeKg, setRondeKg] = useState(
    standaardRondeKg > 0 ? String(Number(standaardRondeKg.toFixed(2))) : "",
  );

  const pctTotaal = ingredienten.reduce((total, item) => total + toNum(item.percentage), 0);
  const gewensteKg = modus === "rondes"
    ? toNum(rondes) * toNum(rondeKg)
    : toNum(doelKg);
  const factor = pctTotaal > 0 ? gewensteKg / pctTotaal : 0;
  const totaleKosten = ingredienten.reduce(
    (total, item) => total + (toNum(item.percentage) * factor * toNum(
      ingredientPrijs(item.categorie, item.naam),
    )),
    0,
  );

  return (
    <section
      className="mt-4 border-l-4 border-emerald-500 bg-emerald-50 px-4 py-4"
      data-testid={`boneless-planner-${recipe.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-emerald-950">Rondeplanner — Boneless Wings</h3>
          <p className="mt-1 text-xs text-emerald-900">
            Schaal de volledige receptuur, inclusief hulpstoffen, zonder de verhoudingen te wijzigen.
          </p>
        </div>
        <div className="text-right text-xs text-emerald-900">
          <div>Receptverhouding: {pctTotaal.toFixed(2)}%</div>
          <div data-testid={`boneless-planner-target-${recipe.id}`}>
            Doelbatch: <b>{kgf(gewensteKg)}</b>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid={`boneless-planner-mode-${recipe.id}`}>
        <button
          type="button"
          data-testid={`boneless-planner-mode-kg-${recipe.id}`}
          className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
            modus === "kg"
              ? "bg-emerald-700 text-white"
              : "border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
          }`}
          onClick={() => setModus("kg")}
        >
          Gewenste kg
        </button>
        <button
          type="button"
          data-testid={`boneless-planner-mode-rondes-${recipe.id}`}
          className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
            modus === "rondes"
              ? "bg-emerald-700 text-white"
              : "border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
          }`}
          onClick={() => setModus("rondes")}
        >
          Rondes × batch
        </button>
      </div>

      {modus === "kg" ? (
        <label className="mt-3 block max-w-xs text-xs font-semibold text-emerald-950">
          Totale gewenste batch (kg)
          <input
            type="number"
            min="0"
            step="any"
            data-testid={`boneless-planner-kg-${recipe.id}`}
            className="mt-1 w-full rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm"
            value={doelKg}
            onChange={(event) => setDoelKg(event.target.value)}
            placeholder="Bijv. 2400"
          />
        </label>
      ) : (
        <div className="mt-3 grid max-w-lg grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-emerald-950">
            Aantal rondes
            <input
              type="number"
              min="0"
              step="any"
              data-testid={`boneless-planner-rondes-${recipe.id}`}
              className="mt-1 w-full rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm"
              value={rondes}
              onChange={(event) => setRondes(event.target.value)}
              placeholder="Bijv. 3"
            />
          </label>
          <label className="text-xs font-semibold text-emerald-950">
            Beschikbare batch per ronde (kg)
            <input
              type="number"
              min="0"
              step="any"
              data-testid={`boneless-planner-rondekg-${recipe.id}`}
              className="mt-1 w-full rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm"
              value={rondeKg}
              onChange={(event) => setRondeKg(event.target.value)}
              placeholder="Bijv. 800"
            />
          </label>
        </div>
      )}

      {pctTotaal <= 0 ? (
        <p data-testid={`boneless-planner-empty-${recipe.id}`} className="mt-3 text-xs text-amber-800">
          Vul eerst de percentages van het recept in om een ronde te kunnen plannen.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-md border border-emerald-200 bg-white">
          <table className="w-full text-sm" data-testid={`boneless-planner-table-${recipe.id}`}>
            <thead className="bg-emerald-100 text-left text-xs text-emerald-950">
              <tr>
                <th className="px-3 py-2 font-semibold">Ingrediënt</th>
                <th className="px-3 py-2 font-semibold">Categorie</th>
                <th className="px-3 py-2 text-right font-semibold">Verhouding</th>
                <th className="px-3 py-2 text-right font-semibold">Nodig</th>
                <th className="px-3 py-2 text-right font-semibold">Kosten</th>
              </tr>
            </thead>
            <tbody>
              {ingredienten.map((item) => {
                const kg = toNum(item.percentage) * factor;
                const kosten = kg * toNum(ingredientPrijs(item.categorie, item.naam));
                return (
                  <tr
                    key={item.id || `${item.categorie}-${item.naam}`}
                    className="border-t border-emerald-100"
                    data-testid={`boneless-planner-row-${recipe.id}-${item.id}`}
                  >
                    <td className="px-3 py-2 text-slate-800">{item.naam || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{item.categorie || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{toNum(item.percentage).toFixed(2)}%</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{kgf(kg)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{euro(kosten)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-emerald-200 bg-emerald-50 text-emerald-950">
              <tr>
                <td className="px-3 py-2 font-semibold" colSpan={3}>Totaal</td>
                <td className="px-3 py-2 text-right font-bold">{kgf(gewensteKg)}</td>
                <td className="px-3 py-2 text-right font-bold">{euro(totaleKosten)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}